import { useState } from 'react';
import { useStok, stokHareketEkle, StokUrun, StokHareket } from './hooks/useStok';
import { useAuth } from '@/contexts/AuthContext';

type StokDurum = 'normal' | 'dusuk' | 'kritik';

function getStokDurum(urun: StokUrun): StokDurum {
  if (urun.stok_miktari === 0) return 'kritik';
  if (urun.stok_miktari <= urun.minimum_stok_seviyesi) return 'kritik';
  if (urun.stok_miktari <= urun.minimum_stok_seviyesi * 1.5) return 'dusuk';
  return 'normal';
}

const durumCfg = {
  normal:  { label: 'Normal', badge: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500' },
  dusuk:   { label: 'Düşük',  badge: 'bg-yellow-100 text-yellow-700',   bar: 'bg-yellow-400' },
  kritik:  { label: 'Kritik!', badge: 'bg-red-100 text-red-700',         bar: 'bg-red-500' },
};

const kategoriler = ['Tümü', 'Filtre', 'Membran', 'Musluk', 'Tank', 'Aksesuar'];

interface StokHareketModalProps {
  urun: StokUrun;
  onClose: () => void;
  onSaved: () => void;
}

function StokHareketModal({ urun, onClose, onSaved }: StokHareketModalProps) {
  const [miktar, setMiktar] = useState('');
  const [tip, setTip] = useState<'giris' | 'cikis'>('giris');
  const [aciklama, setAciklama] = useState('');
  const [saving, setSaving] = useState(false);
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-sky-400 bg-white';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!miktar || Number(miktar) <= 0) return;
    setSaving(true);
    try {
      await stokHareketEkle({
        stok_urun_id: urun.id,
        hareket_turu: tip,
        miktar: Number(miktar),
        aciklama,
      });
      onClose();
      onSaved();
    } catch {
      alert('Stok hareketi kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-800">Stok Hareketi</h3>
            <p className="text-xs text-gray-400">{urun.urun_adi}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">İşlem Tipi</label>
            <div className="grid grid-cols-2 gap-2">
              {(['giris', 'cikis'] as const).map(t => (
                <button key={t} type="button" onClick={() => setTip(t)}
                  className={`py-2.5 rounded-xl text-sm font-bold cursor-pointer border-2 transition-all whitespace-nowrap
                    ${tip === t
                      ? t === 'giris' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-red-500 text-white border-red-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                  {t === 'giris' ? '+ Stok Girişi' : '− Stok Çıkışı'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Miktar ({urun.birim}) — Mevcut: <span className="font-bold text-gray-800">{urun.stok_miktari}</span>
            </label>
            <input required type="number" min="1" className={inputCls} value={miktar} onChange={e => setMiktar(e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Açıklama</label>
            <input type="text" className={inputCls} value={aciklama} onChange={e => setAciklama(e.target.value)} placeholder="Tedarikçi alımı, servis kullanımı..." />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
            <button type="submit" disabled={saving}
              className={`flex-1 py-2.5 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap transition-colors disabled:opacity-50 ${tip === 'giris' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}`}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StokPage() {
  const { urunler, hareketler, loading, error, refetch } = useStok();
  const { isAdmin, isOffice } = useAuth();
  const [kategori, setKategori] = useState('Tümü');
  const [search, setSearch] = useState('');
  const [hareketModal, setHareketModal] = useState<StokUrun | null>(null);

  const filtrelenmis = urunler.filter(u => {
    const matchKat = kategori === 'Tümü' || u.urun_kategorisi === kategori;
    const matchSearch = u.urun_adi.toLowerCase().includes(search.toLowerCase());
    return matchKat && matchSearch;
  });

  const kritikler = urunler.filter(u => getStokDurum(u) === 'kritik').length;
  const dusukler = urunler.filter(u => getStokDurum(u) === 'dusuk').length;
  const toplamDeger = urunler.reduce((s, u) => s + u.stok_miktari * u.satis_fiyati, 0);

  if (loading) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
        <p className="text-sm text-gray-500 mt-3">Stok verileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <p className="text-sm text-red-700 font-medium">Veriler yüklenemedi.</p>
        <button onClick={refetch} className="mt-3 text-sm text-red-600 font-semibold cursor-pointer">Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Stok Yönetimi</h2>
          <p className="text-sm text-gray-500 mt-0.5">Filtre, membran ve aksesuar stok takibi</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Ürün', val: urunler.length, icon: 'ri-store-2-line', bg: 'bg-sky-100', color: 'text-sky-600' },
          { label: 'Kritik Stok', val: kritikler, icon: 'ri-alarm-warning-line', bg: 'bg-red-100', color: 'text-red-500' },
          { label: 'Düşük Stok', val: dusukler, icon: 'ri-time-line', bg: 'bg-yellow-100', color: 'text-yellow-600' },
          { label: 'Stok Değeri', val: `₺${toplamDeger.toLocaleString('tr-TR')}`, icon: 'ri-money-dollar-circle-line', bg: 'bg-emerald-100', color: 'text-emerald-600' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-start justify-between">
              <div><p className="text-xs text-gray-500 font-medium">{k.label}</p><p className="text-xl font-bold text-gray-800 mt-1">{k.val}</p></div>
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${k.bg}`}><i className={`${k.icon} text-lg ${k.color}`}></i></div>
            </div>
          </div>
        ))}
      </div>

      {(kritikler > 0 || dusukler > 0) && (
        <div className={`rounded-xl border px-5 py-3.5 flex items-center gap-3 ${kritikler > 0 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`ri-alarm-warning-line text-lg ${kritikler > 0 ? 'text-red-500' : 'text-yellow-500'}`}></i>
          </div>
          <p className={`text-sm font-medium ${kritikler > 0 ? 'text-red-800' : 'text-yellow-800'}`}>
            {kritikler > 0 && <><span className="font-bold">{kritikler} üründe</span> kritik stok seviyesi! </>}
            {dusukler > 0 && <><span className="font-bold">{dusukler} üründe</span> stok düşük.</>}
            {' '}Tedarikçiyle iletişime geçin.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative w-full sm:w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-sky-400 bg-white"
            placeholder="Ürün ara..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {kategoriler.map(k => (
            <button key={k} onClick={() => setKategori(k)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap
                ${kategori === k ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>{k}</button>
          ))}
        </div>
      </div>

      {filtrelenmis.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-store-2-line text-2xl text-slate-400"></i>
          </div>
          <p className="text-sm text-gray-500 font-medium">Henüz stok kaydı bulunmuyor.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ürün</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Kategori</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Miktar</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Stok Durumu</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Satış Fiyatı</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtrelenmis.map(u => {
                  const durum = getStokDurum(u);
                  const cfg = durumCfg[durum];
                  const barPct = Math.min(100, Math.round((u.stok_miktari / (u.minimum_stok_seviyesi * 3)) * 100));
                  return (
                    <tr key={u.id} className={`hover:bg-slate-50 transition-colors ${durum === 'kritik' ? 'bg-red-50/30' : ''}`}>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-semibold text-gray-800">{u.urun_adi}</p>
                        <p className="text-xs text-gray-400 sm:hidden">{u.urun_kategorisi}</p>
                      </td>
                      <td className="px-4 py-3.5 text-center hidden sm:table-cell">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">{u.urun_kategorisi}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`text-base font-bold ${durum === 'kritik' ? 'text-red-600' : durum === 'dusuk' ? 'text-yellow-600' : 'text-gray-800'}`}>{u.stok_miktari}</span>
                        <span className="text-xs text-gray-400 ml-1">{u.birim}</span>
                        <p className="text-xs text-gray-400">min: {u.minimum_stok_seviyesi}</p>
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-2 rounded-full transition-all ${cfg.bar}`} style={{ width: `${barPct}%` }} />
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.badge}`}>{cfg.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right hidden sm:table-cell">
                        <span className="text-sm text-gray-600">₺{u.satis_fiyati}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <button onClick={() => setHareketModal(u)}
                          className="flex items-center gap-1.5 text-xs bg-sky-50 hover:bg-sky-100 text-sky-700 font-semibold px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                          <i className="ri-add-line"></i> Hareket
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Son Hareketler */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">Son Stok Hareketleri</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-slate-50">
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tarih</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ürün</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlem</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Miktar</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Açıklama</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {hareketler.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-sm text-gray-400">Henüz stok hareketi bulunmuyor.</td></tr>
              ) : hareketler.map(h => (
                <tr key={h.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3"><span className="text-xs text-gray-500">{new Date(h.created_at).toLocaleDateString('tr-TR')}</span></td>
                  <td className="px-4 py-3"><span className="text-sm font-medium text-gray-700">{h.urun_adi}</span></td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${h.hareket_turu === 'giris' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                      <i className={h.hareket_turu === 'giris' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'}></i>
                      {h.hareket_turu === 'giris' ? 'Giriş' : 'Çıkış'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-bold ${h.hareket_turu === 'giris' ? 'text-emerald-600' : 'text-red-500'}`}>
                      {h.hareket_turu === 'giris' ? '+' : '-'}{h.miktar}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell"><span className="text-xs text-gray-500">{h.aciklama}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hareketModal && <StokHareketModal urun={hareketModal} onClose={() => setHareketModal(null)} onSaved={refetch} />}
    </div>
  );
}